// O COMBO DE ETIQUETAS DA VENDA — campo fechado + painel flutuante ([DA-722]).
//
// ⚠️ **Sobrou UM consumidor** ([DA-723], 12/09/2026): o caminho 1, com o cliente ja escolhido —
// ali o modal volta a UMA coluna de 764px e nao ha onde por uma lista aberta. No caminho 2 (o de
// tres colunas) a lista virou o `<ListboxEtiquetasVenda>`, sempre a vista no meio do modal.
//
// ⛔ **COMBO, nao grade solta — e a palavra e' dele** (*"a relacao de etiqueta do COMBO"*). Medido
// na tela publicada: a grade de 14 chips cai em 2 colunas dentro do bloco e faz o miolo do modal
// rolar 265px. Fechado o campo custa UMA linha; a lista abre por cima, em painel `fixed`, e nao
// empurra nada. ⚠️ Isto segue valendo aqui — na coluna unica nao ha largura sobrando.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Tag, Lock, ChevronDown } from 'lucide-react';
import { SeletorEtiquetas } from '../../modules/atendimento/componentes/SeletorEtiquetas';
import { corDoTexto } from '../../modules/atendimento/componentes/BadgeEtiqueta';
import type { Etiqueta } from '../../modules/atendimento/lib/etiquetas-context';
import { marcadasNaTela, travadasNaTela, type EstadoEtiquetas } from './etiquetas-do-balcao';

interface Props {
  /** So' as que podem ser alvo (`paraCampanha`), JA em ordem alfabetica. */
  etiquetas: Etiqueta[];
  estado: EstadoEtiquetas;
  onAlternar: (id: number) => void;
  ehAdmin: boolean;
  desabilitado?: boolean;
  /** Marca o campo com `*` — hoje so' o caminho 2 exige ([DA-722] p.10). */
  obrigatorio?: boolean;
  /** Chamado quando o painel abre, pra quem hospeda fechar os proprios dropdowns. */
  onAbrir?: () => void;
  /** Fecha o painel de fora (o hospedeiro abriu outro dropdown). */
  fecharSinal?: number;
  /** Texto do campo vazio quando quem hospeda ja tem rotulo proprio (a tela do fly). */
  instrucao?: string;
  /** Altura minima pra casar com o campo vizinho (na tela do fly, o Nome ao lado mede 36px). */
  alturaMin?: number;
}

export function ComboEtiquetas({
  etiquetas, estado, onAlternar, ehAdmin, desabilitado, obrigatorio, onAbrir, fecharSinal, instrucao, alturaMin,
}: Props) {
  const [aberto, setAberto] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [caixa, setCaixa] = useState<{ left: number; top: number; width: number; alturaMax: number } | null>(null);

  // ⛔ So' as que esta lista DESENHA: a etiqueta oculta do rodizio (CRISTINA, GABRYELA) vem nas
  // gravadas e nao esta aqui — contando ela, o campo escondia o texto de instrucao e ficava em
  // branco, sem pill nenhuma (contato 52651, medido 24/09/2026 na tela do fly).
  const marcadas = marcadasNaTela(estado).filter((id) => etiquetas.some((e) => e.id === id));
  const travadas = travadasNaTela(estado, ehAdmin);
  const temTravada = travadas.some((id) => etiquetas.some((e) => e.id === id));

  const medir = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    // ⚠️ **A altura se MEDE contra a janela, nunca se crava** ([DA-704]): em coluna unica sao 14
    // linhas de ~34px, e um teto fixo faria o painel rolar sempre.
    const desejada = Math.max(220, Math.min(560, window.innerHeight - 120));
    // ⛔ O teto de cima e' o do MODAL, nao o da janela: o `telaCheia` comeca abaixo da barra de
    // menus, e o painel aberto pra cima ficava com o topo escondido atras dela (tela do fly,
    // 24/09/2026). Sem modal em volta o overlay nao existe e o teto volta a ser a janela.
    const overlay = btnRef.current?.closest('.modal-overlay');
    const teto = Math.max(8, (overlay?.getBoundingClientRect().top ?? 0) + 8);
    const espacoAbaixo = window.innerHeight - r.bottom - 12;
    const espacoAcima = r.top - teto - 4;
    // Cabe inteiro embaixo? vai embaixo. Senao, o lado com mais espaco — e a altura CEDE ao
    // espaco que ha, com a lista rolando por dentro (`overflowY: auto` do painel).
    const abaixo = espacoAbaixo >= desejada || espacoAbaixo >= espacoAcima;
    const alturaMax = Math.min(desejada, Math.max(160, abaixo ? espacoAbaixo : espacoAcima));
    setCaixa({
      left: Math.max(8, Math.min(r.left, window.innerWidth - r.width - 8)),
      top: abaixo ? r.bottom + 4 : Math.max(teto, r.top - alturaMax - 4),
      width: Math.max(r.width, 320),
      alturaMax,
    });
  }, []);

  // Fecha ao rolar/redimensionar: a caixa foi medida uma vez e ficaria pendurada no lugar errado.
  // ⛔ Menos a rolagem da PROPRIA lista (relato dele, 24/09/2026: *"quando tento rolar o combo,
  // ela some"*): o listener e' de captura, entao o scroll do painel tambem chega aqui — e fechar
  // nele tornava inalcancavel toda etiqueta abaixo da dobra.
  const painelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!aberto) return;
    const fechar = () => setAberto(false);
    const aoRolar = (e: Event) => {
      if (e.target instanceof Node && painelRef.current?.contains(e.target)) return;
      setAberto(false);
    };
    window.addEventListener('resize', fechar);
    window.addEventListener('scroll', aoRolar, true);
    return () => { window.removeEventListener('resize', fechar); window.removeEventListener('scroll', aoRolar, true); };
  }, [aberto]);

  useEffect(() => { if (fecharSinal) setAberto(false); }, [fecharSinal]);

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={() => { onAbrir?.(); medir(); setAberto((v) => !v); }}
        aria-expanded={aberto}
        disabled={desabilitado}
        className="w-full px-2 py-1.5 border border-gray-300 rounded bg-white text-left flex items-center gap-1.5 min-h-[34px] disabled:opacity-50"
        style={alturaMin ? { minHeight: alturaMin } : undefined}
      >
        <Tag size={12} className="flex-shrink-0 text-gray-500" />
        {marcadas.length === 0 ? (
          instrucao ? (
            <span className="text-[12px] text-gray-500 truncate">{instrucao}</span>
          ) : (
            <span className="text-[12px] text-gray-500">
              Etiquetas {obrigatorio && <span className="text-red-600 font-bold">*</span>} — a area de atuacao de quem esta comprando
            </span>
          )
        ) : (
          // ⛔ **TODAS as pills, sempre** (pedido dele em 12/09/2026: *"quando o contato tem mais de
          // uma Etiqueta selecionada, deve exibir todas as Etiquetas dele"*). Elas quebram linha e
          // o campo cresce — e' o preco, e hoje ele cabe: o modal em duas colunas fechou em 468px
          // numa janela de 740, entao ha folga que nao havia quando o teto de duas foi posto.
          // ⚠️ **Isto e' o que muda a altura do campo; se um dia o modal voltar a rolar, o culpado
          // e' aqui** — e a saida NAO e' voltar ao `+N`, e' achar a altura noutro lugar.
          <span className="flex-1 min-w-0 flex flex-wrap gap-1">
            {marcadas.map((id) => {
              const e = etiquetas.find((x) => x.id === id);
              if (!e) return null;
              const travada = travadas.includes(id);
              return (
                <span key={id} className="text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
                  style={{ backgroundColor: e.cor || '#e2e8f0', color: corDoTexto(e.cor || '#e2e8f0') }}>
                  {travada && <Lock size={8} strokeWidth={3} />}
                  {e.apelido || e.nome}
                </span>
              );
            })}
          </span>
        )}
        <ChevronDown size={13} className="flex-shrink-0 text-gray-500 ml-auto" />
      </button>

      {aberto && caixa && (
        <>
          <div className="fixed inset-0 z-[1190]" onMouseDown={() => setAberto(false)} />
          <div
            ref={painelRef}
            className="fixed z-[1200] rounded-lg border border-gray-300 bg-white shadow-xl p-2.5"
            style={{ left: caixa.left, top: caixa.top, width: caixa.width, maxHeight: caixa.alturaMax, overflowY: 'auto', overscrollBehavior: 'contain' }}
          >
            <div className="flex items-baseline justify-between gap-2 mb-2">
              <span className="text-[10px] font-bold uppercase text-gray-500">
                Area de atuacao · marque quantas precisar
              </span>
              <button type="button" onClick={() => setAberto(false)}
                className="text-[11px] font-semibold text-blue-700 hover:underline flex-shrink-0">
                Fechar
              </button>
            </div>
            <SeletorEtiquetas
              etiquetas={etiquetas}
              marcadas={marcadas}
              travadas={travadas}
              onAlternar={onAlternar}
              desabilitado={desabilitado}
              umaColuna
            />
            {/* ⚠️ O chip travado ja diz por que no `title`, mas tooltip exige mouse — e o balcao e'
                tablet. A linha so' nasce quando ha alguma travada de verdade nesta grade. */}
            {temTravada && (
              <div className="text-[11px] text-gray-600 mt-2 pt-2 border-t border-gray-200 flex items-start gap-1.5">
                <Lock size={11} className="flex-shrink-0 mt-[2px]" />
                <span>
                  As etiquetas com cadeado ja estavam gravadas neste contato. Da' pra acrescentar,
                  e so' um administrador retira.
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
